import {useContext, useEffect, useRef, useState} from 'react';
import WaveSurfer from 'wavesurfer.js';
import Styles from "../styles/pages/AudioEdit.module.css";
import mp3Parser from 'mp3-parser';
import { AuthContext } from "../AuthContext";
import { Navigate } from "react-router-dom";
import { isUserInRole } from "../pr_utils";



const AudioTrimmer = () => {
    const waveformRef = useRef(null);
    const wavesurferRef = useRef(null);
    const [markerValue, setMarkerValue] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(100);
    const [trimStart, setTrimStart] = useState(null);
    const [trimEnd, setTrimEnd] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [parsedHeaders, setParsedHeaders] = useState([]);
    const [cursor, setCursor] = useState(0.0);
    const [duration, setDuration] = useState('-');
    const [filename, setFilename] = useState(null);
    const { authStatus } = useContext(AuthContext);


    useEffect(() => {


        if(!authStatus) return;

        wavesurferRef.current = WaveSurfer.create({
            container: waveformRef.current,
            scrollParent:true,
            waveColor: 'gray',
            progressColor: 'purple',
            plugins: [/* add regions plugin here */],
        });

        // Load an MP3 file (upload or URL)
        //wavesurferRef.current.load('test2.mp3');


        wavesurferRef.current.on('ready', () => {
            console.log('Waveform is ready!');
            wavesurferRef.current.zoom(100); // Safe to zoom now
        });

        wavesurferRef.current.on('timeupdate', () => {
            setCursor(formatTime(wavesurferRef.current.getCurrentTime()));
        });

        //wavesurfer.zoom(100);

        wavesurferRef.current.on('interaction', (event) => {
            // const time = wavesurferRef.current.getCurrentTime();
            // const minutes = Math.floor(time / 60);
            // const seconds = (time % 60).toFixed(1).padStart(4, '0'); // 1 decimal
            //
            // console.log(time);
            // console.log(`User clicked at: ${minutes}:${seconds}`);
            // setMarkerValue(time);
            // wavesurferRef.current.play();
        });


        // Add region selection and button for trimming...

        return () => wavesurferRef.current.destroy();
    }, [authStatus]);

    useEffect(() => {
        if(trimStart && trimEnd) {
            setDuration(formatTime(trimEnd - trimStart));
        }
    }, [trimStart, trimEnd]);

    const handleZoom = (direction) => {
        if (direction === 'reset') {
            setZoomLevel(100);
            wavesurferRef.current.zoom(100);
            return;
        }

        const zoomFactor = direction === 'in' ? 1.1 : 0.9;
        const newZoom = Math.max(10, zoomLevel * zoomFactor);
        setZoomLevel(newZoom);
        wavesurferRef.current.zoom(newZoom);
    };

    const seekBy = (seconds) => {
        const duration = wavesurferRef.current.getDuration();
        const current = wavesurferRef.current.getCurrentTime();
        const target = Math.max(0, Math.min(duration, current + seconds));
        seekToTime(target);
    };

    const seekToTime = (targetTime) => {
        const duration = wavesurferRef.current.getDuration();
        const percent = targetTime / duration;
        wavesurferRef.current.seekTo(percent);
    };
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];

        const match = file.name.match(/^(\d{4})_(\d{2})_(\d{2})_/);
        if (match) {
            const [, year, month, day] = match;
            setFilename(`${year}-${month}-${day}`);
        } else {
            setFilename(null);
        }


        if (!file) return ;

        setSelectedFile(file);
        const objectUrl = URL.createObjectURL(file);
        wavesurferRef.current.load(objectUrl);

        const arrayBuffer = await file.arrayBuffer();      // Read file into memory
        const view = new DataView(arrayBuffer);            // Wrap for mp3-parser
        const parsedTags = mp3Parser.readTags(view);       // Run tag/frame parser
        console.log('Parsed MP3 Tags:', parsedTags);       // Output to console
        setParsedHeaders(parsedTags);

        // Check for likely VBR indicators
        const vbrTagFound = parsedTags.some(tag =>
            tag.identifier === 'Xing' || tag.identifier === 'VBRI'
        );

        if (vbrTagFound) {
            alert("Warning: This MP3 appears to use Variable Bitrate (VBR). Trimming may be less accurate.");
        }

        // (Optional: Inspect first frame)
        const firstFrame = mp3Parser.readFrame(view);
        console.log('First frame info:', firstFrame);

    };




    const splitTimeComponents = (totalSeconds) => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);
        const fractional_seconds = Math.round((totalSeconds % 1) * 100000); // preserve 5 digits

        return {
            hours,
            minutes,
            seconds,
            fractional_seconds
        };
    }
    const formatTime = (totalSeconds) => {
        const t = splitTimeComponents(totalSeconds);

        return t['hours'].toString().padStart(2, "0") + ":" +
            t['minutes'].toString().padStart(2, "0") + ":" +
            t['seconds'].toString().padStart(2, "0") + "." +
            t['fractional_seconds'].toString().padEnd(5, "0");
    }

    const renderFlatKeyValues = (obj, parentKey = '') => {
        return Object.entries(obj).flatMap(([key, value]) => {
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            if (key === '_section') return []; // Already handled above

            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                return renderFlatKeyValues(value, fullKey); // Recurse into nested objects
            }

            if (Array.isArray(value)) {
                return value.map((item, i) =>
                    typeof item === 'object'
                        ? renderFlatKeyValues(item, `${fullKey}[${i}]`)
                        : (
                            <div key={`${fullKey}[${i}]`}>
                                <strong>{`${fullKey}[${i}]`}:</strong> {item.toString()}
                            </div>
                        )
                );
            }

            return (
                <div key={fullKey}>
                    <strong>{fullKey}:</strong> {value?.toString()}
                </div>
            );
        });
    };

    const estimateOffset = (timeInSeconds, bitrateKbps) => {
        const bytesPerSecond = (bitrateKbps * 1000) / 8;
        return Math.floor(timeInSeconds * bytesPerSecond);
    };

    const trimMp3ByTime = async (file, startTime, endTime) => {

        const arrayBuffer = await file.arrayBuffer();
        const view = new DataView(arrayBuffer);

        let offset = 0;
        const frames = [];
        let cumulativeTime = 0;
        let startOffset = null;
        let endOffset = null;
        let collecting = false;

        while (offset < view.byteLength) {
            const frame = mp3Parser.readFrame(view, offset, false);
            if (!frame) {
                offset++;
                continue;
            }

            const { samplingRate } = frame.header;
            const sampleCount = frame.sampleLength || 1152; // Assume 1152 if not specified
            const frameDuration = sampleCount / samplingRate;

            if (cumulativeTime >= startTime && !collecting) {
                startOffset = frame._section.offset;
                collecting = true;
            }

            if (collecting) {
                frames.push(frame);
            }

            cumulativeTime += frameDuration;

            if (cumulativeTime >= endTime) {
                endOffset = frame._section.offset + frame._section.byteLength;
                break;
            }

            offset = frame._section.offset + frame._section.byteLength;
        }

        if (startOffset === null || endOffset === null) {
            console.error("Could not find a valid trimming range.");
            return;
        }

        const trimmedBuffer = arrayBuffer.slice(startOffset, endOffset);
        const blob = new Blob([trimmedBuffer], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename ? filename + '-trimmed.mp3' : `trimmed_${startTime.toFixed(2)}-${endTime.toFixed(2)}.mp3`;
        a.click();
    };


    const trimMp3ByTimeOld = async (file, startTime, endTime) => {

        startTime = 0;
        endTime = 2;

        const arrayBuffer = await file.arrayBuffer();
        const view = new DataView(arrayBuffer);

        const tags = mp3Parser.readTags(view);
        const firstFrame = tags.find(tag => tag._section?.type === 'frame');
        const bitrate = firstFrame?.header?.bitrate || 128;

        console.log("BITRATE: " + bitrate);

        // Estimate byte offsets
        const bytesPerSecond = (bitrate * 1000) / 8;

        let startOffset = Math.floor(startTime * bytesPerSecond);
        let endOffset = Math.ceil(endTime * bytesPerSecond);

        // Find real start frame
        let realStartOffset = startOffset;
        let frame = null;
        while (realStartOffset < view.byteLength) {
            frame = mp3Parser.readFrame(view, realStartOffset, false);
            if (frame) break;
            realStartOffset++;
        }

        const bytesPerSecondEstimate = frame.header.bitrate * 1000 / 8;


        console.log("REAL START OFFSET: " + realStartOffset);
        console.log("view.byteLength: " + view.byteLength);

        // Same for end
        let realEndOffset = endOffset;
        let accumulatedDuration = 0;
        let endFound = false;

        while (realEndOffset < view.byteLength) {
            const temp = mp3Parser.readFrame(view, realEndOffset, false);
            if (temp) {
                const frameDuration = temp.sampleLength / temp.header.samplingRate;
                accumulatedDuration += frameDuration;

                if ((temp._section.offset / bytesPerSecondEstimate) >= endTime || accumulatedDuration >= (endTime - startTime)) {
                    frame = temp;
                    realEndOffset = temp._section.offset + temp._section.byteLength;
                    endFound = true;
                    break;
                }

                realEndOffset = temp._section.offset + temp._section.byteLength;
            } else {
                realEndOffset++;
            }
        }


        // Slice the trimmed section
        const trimmedBuffer = arrayBuffer.slice(realStartOffset, realEndOffset);
        const blob = new Blob([trimmedBuffer], { type: 'audio/mpeg' });

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'trimmed.mp3';
        a.click();
    };



    const trimMp3Approx10Seconds = async (file) => {
        console.log(mp3Parser);

        const arrayBuffer = await file.arrayBuffer();
        const view = new DataView(arrayBuffer);

        // Parse all valid frames
        const frames = [];
        let offset = 0;

        // Skip tags
        const id3 = mp3Parser.readId3v2Tag(view, offset);
        if (id3) {
            offset += id3._section.byteLength;
        }

        const xing = mp3Parser.readXingTag(view, offset);
        if (xing) {
            offset = xing._section.nextFrameIndex; // This is the start of first real audio
        } else {
            // fallback: try readFrame repeatedly until success
            while (!mp3Parser.readFrame(view, offset)) {
                offset += 1;
            }
        }


        console.log("Offset: " + offset);

        console.log("Byte Length: " + view.byteLength);

        while (offset < view.byteLength) {
            const frame = mp3Parser.readFrame(view, offset, true);
            console.log(frame);
            if (!frame) break;
            frames.push(frame);
            offset += frame._section.byteLength;
        }

        if (frames.length === 0) {
            console.error("No MP3 frames found");
            return;
        }

        // Estimate total duration using frame count (each MP3 frame = ~1152 samples)
        const sampleRate = frames[0]?.header?.samplingRate || 44100;
        const frameDuration = 1152 / sampleRate; // seconds per frame
        const totalDuration = frames.length * frameDuration;

        console.log(`Estimated duration: ${totalDuration.toFixed(2)} seconds`);

        // Calculate frames for ~10 seconds in the middle
        const tenSecondFrameCount = Math.round(10 / frameDuration);
        const startFrame = Math.max(0, Math.floor((frames.length - tenSecondFrameCount) / 2));
        const endFrame = startFrame + tenSecondFrameCount;

        const trimmedBytes = [];

        for (let i = startFrame; i < endFrame && i < frames.length; i++) {
            const frame = frames[i];
            const frameBytes = new Uint8Array(arrayBuffer, frame._section.offset, frame._section.byteLength);
            trimmedBytes.push(...frameBytes);
        }

        // Create a blob and download it
        const trimmedBuffer = new Uint8Array(trimmedBytes);
        const blob = new Blob([trimmedBuffer], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'trimmed.mp3';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };




    // Block unauthenticated users or non-admins
    if (!authStatus || !isUserInRole(authStatus, ['ADMIN'])) {
        return <Navigate to="/" />;
    }


    return (
        <>


            <div ref={waveformRef}></div>

            <div className={Styles.controlPanel}>

                <div className = {Styles.controlPanelButtonRow}>
                    <button onClick={() => setTrimStart(wavesurferRef.current.getCurrentTime())}>Set as Trim Start</button>
                    <button onClick={() => setTrimEnd(wavesurferRef.current.getCurrentTime())}>Set as Trim End</button>
                </div>
                <div className={Styles.markerSpan}>
                    Cursor at: {cursor}
                </div>
                <div className={Styles.markerSpan}>
                    Trim Start: {formatTime(trimStart)}
                </div>
                <div className={Styles.markerSpan}>
                    Trim End: {formatTime(trimEnd)}
                </div>
                <div className={Styles.markerSpan}>
                    Duration: {duration}
                </div>
                <div className = {Styles.controlPanelButtonRow}>
                    <button onClick={() => trimMp3ByTime(selectedFile, trimStart, trimEnd)}>Export Selection</button>
                </div>
            </div>
            <div className={Styles.controlPanel}>
                <div className={Styles.controlPanelButtonRow}>
                    <button onClick={() => seekToTime(0)}>⏮️</button>
                    <button onClick={() => wavesurferRef.current.play()}>▶️</button>
                    <button onClick={() => wavesurferRef.current.pause()}>⏸️</button>
                    <button onClick={() => seekToTime(wavesurferRef.current.getDuration())}>⏭️</button>
                </div>
                <div className={Styles.controlPanelButtonRow}>
                    <button onClick={() => seekBy(-1)}>⏪ 1s</button>
                    <button onClick={() => seekBy(-10)}>⏪ 10s</button>
                    <button onClick={() => seekBy(-60)}>⏪ 1m</button>
                    <button onClick={() => seekBy(-300)}>⏪ 5m</button>
                </div>

                <div className={Styles.controlPanelButtonRow}>
                    <button onClick={() => seekBy(1)}>⏩ 1s</button>
                    <button onClick={() => seekBy(10)}>⏩ 10s</button>
                    <button onClick={() => seekBy(60)}>⏩ 1m</button>
                    <button onClick={() => seekBy(300)}>⏩ 5m</button>
                </div>
                <div className={Styles.controlPanelButtonRow}>
                    <button onClick={() => handleZoom('in')}>Zoom In 10%</button>
                    <button onClick={() => handleZoom('reset')}>Reset Zoom</button>
                    <button onClick={() => handleZoom('out')}>Zoom Out 10%</button>
                </div>
                <div className={Styles.fileInputWrapper}>
                    <label htmlFor="audio-upload" className={Styles.fileInputLabel}>Choose MP3:</label>
                    <input
                        type="file"
                        accept="audio"
                        id="audio-upload"
                        onChange={handleFileUpload}
                    />
                </div>
            </div>

            {parsedHeaders && (
                <div className={Styles.headerInfoWrapper}>
                    <h3>MP3 Header Info</h3>
                    {parsedHeaders.map((section, index) => (
                        <div key={index} className={Styles.headerBlock}>
                            <strong>Section Type:</strong> {section._section?.type || 'Unknown'}
                            <div className={Styles.kvList}>
                                {renderFlatKeyValues(section)}
                            </div>
                        </div>
                    ))}
                </div>
            )}




        </>
    );
};

export default AudioTrimmer;